import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Pencil, Trash2 } from "lucide-react";
import ShareButton from "@/components/ShareButton";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Markdown component overrides. Defined OUTSIDE the page component so React doesn't
// re-create them on every render (eslint react/no-unstable-nested-components).
const mkComponents = (isRTL, serifClass) => ({
  h1: (p) => <h1 className={`${serifClass} text-3xl lg:text-4xl text-[#2A2421] mt-10 mb-4 leading-tight`} {...p} />,
  h2: (p) => <h2 className={`${serifClass} text-2xl lg:text-3xl text-[#2A2421] mt-10 mb-3 leading-tight`} {...p} />,
  h3: (p) => <h3 className={`${serifClass} text-xl lg:text-2xl text-[#2A2421] mt-8 mb-2 leading-tight`} {...p} />,
  p: (p) => <p className="my-4 leading-[1.85]" {...p} />,
  ul: ({ ordered, ...p }) => <ul className={`my-4 space-y-2 ${isRTL ? "pr-5" : "pl-5"} list-disc marker:text-[#C86240]`} {...p} />,
  ol: ({ ordered, ...p }) => <ol className={`my-4 space-y-2 ${isRTL ? "pr-5" : "pl-5"} list-decimal marker:text-[#C86240]`} {...p} />,
  li: ({ ordered, checked, ...p }) => <li className="leading-[1.7]" {...p} />,
  strong: (p) => <strong className="font-semibold text-[#2A2421]" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  blockquote: (p) => (
    <blockquote className={`my-6 ${isRTL ? "border-r-4 pr-4" : "border-l-4 pl-4"} border-[#C86240] text-[#5C544F] italic`} {...p} />
  ),
  hr: () => <hr className="my-8 border-[#2A2421]/15" />,
  a: (p) => <a className="text-[#C86240] underline underline-offset-4 hover:text-[#b15637]" {...p} />,
  code: ({ inline, ...p }) => inline
    ? <code className="px-1.5 py-0.5 rounded bg-[#F9F6F0] text-[#C86240] text-[0.92em]" {...p} />
    : <code className="block p-4 rounded-lg bg-[#F9F6F0] text-sm overflow-x-auto" {...p} />,
  table: (p) => (
    <div className="my-6 overflow-x-auto">
      <table className="w-full border-collapse text-sm" {...p} />
    </div>
  ),
  th: (p) => <th className="text-start px-3 py-2 bg-[#F9F6F0] border border-[#2A2421]/15 font-semibold" {...p} />,
  td: (p) => <td className="px-3 py-2 border border-[#2A2421]/10" {...p} />,
});

export default function BlogPost() {
  const { slug } = useParams();
  const { lang, isRTL, t } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = useAdmin();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["blog", slug],
    queryFn: () => endpoints.getBlogPost(slug),
    retry: false,
  });

  // Dynamic SEO is now handled via the <Seo> component below.

  const onDelete = async () => {
    if (!window.confirm(isRTL ? "حذف هذا المقال نهائياً؟" : "Delete this post permanently?")) return;
    try {
      await endpoints.deleteBlogPost(post.id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      qc.invalidateQueries({ queryKey: ["blog"] });
      navigate("/blog");
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Could not delete");
    }
  };

  if (isLoading) {
    return <div className="max-w-3xl mx-auto p-12 text-[#5C544F]">{t.common.loading}</div>;
  }
  if (isError || !post) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center" data-testid="post-not-found">
        <div className="text-[#5C544F] mb-4">{isRTL ? "المقال غير موجود" : "Post not found"}</div>
        <Link to="/blog"><Button variant="outline">{isRTL ? "رجوع للمدوّنة" : "Back to blog"}</Button></Link>
      </div>
    );
  }

  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-8 lg:pt-12 pb-24" data-testid="blog-post-page">
      <Seo
        title={lang === "ar" ? post.title_ar : post.title_en}
        description={lang === "ar" ? (post.excerpt_ar || "") : (post.excerpt_en || "")}
        keywords={`مقال قهوة, مدوّنة القهوة المختصة, ${lang === "ar" ? post.title_ar : post.title_en}, علم القهوة, تعلم القهوة`}
        path={`/blog/${post.slug}`}
        image={post.cover_image || undefined}
        type="article"
        jsonLdId="blogpost-jsonld"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": lang === "ar" ? post.title_ar : post.title_en,
          "description": lang === "ar" ? (post.excerpt_ar || "") : (post.excerpt_en || ""),
          "image": post.cover_image || "https://abdullah-coffee-science.academy/og-image.jpg",
          "datePublished": post.created_at,
          "dateModified": post.updated_at || post.created_at,
          "author": { "@type": "Person", "name": "Abdullah Cassani", "url": "https://abdullah-coffee-science.academy/about" },
          "publisher": { "@type": "Organization", "name": "Abdullah Coffee Science Academy", "logo": { "@type": "ImageObject", "url": "https://abdullah-coffee-science.academy/icon-512.png" } },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://abdullah-coffee-science.academy/blog/${post.slug}` },
          "inLanguage": lang === "ar" ? "ar" : "en"
        }}
      />
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 text-sm text-[#5C544F] hover:text-[#2A2421] mb-8"
        data-testid="back-to-blog"
      >
        <BackIcon className="w-4 h-4" />
        {isRTL ? "كل المقالات" : "All posts"}
      </Link>

      {post.tags?.length > 0 && (
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C86240] mb-4">
          {post.tags.join(" · ")}
        </div>
      )}

      <h1 className={`${serifClass} text-4xl lg:text-5xl text-[#2A2421] leading-tight mb-4`}>
        {lang === "ar" ? post.title_ar : post.title_en}
      </h1>

      <div className="text-sm text-[#5C544F] mb-8 flex flex-wrap items-center gap-3">
        <span>{post.author_name}</span>
        <span>·</span>
        <time>
          {new Date(post.created_at).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric", month: "long", day: "numeric",
          })}
        </time>
        <ShareButton
          title={lang === "ar" ? post.title_ar : post.title_en}
          url={typeof window !== "undefined" ? window.location.href : ""}
        />
        {isAdmin && (
          <>
            <Button
              onClick={() => navigate(`/blog/${post.id}/edit`)}
              variant="ghost"
              size="sm"
              data-testid="edit-post-btn"
            >
              <Pencil className="w-4 h-4 me-1" /> {isRTL ? "تعديل" : "Edit"}
            </Button>
            <Button onClick={onDelete} variant="ghost" size="sm" className="text-red-600" data-testid="delete-post-btn">
              <Trash2 className="w-4 h-4 me-1" /> {isRTL ? "حذف" : "Delete"}
            </Button>
          </>
        )}
      </div>

      {post.cover_image && (
        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10 bg-[#F9F6F0]">
          <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <div
        className="text-[#2A2421] leading-[1.85] text-[17px] lg:text-[18px] space-y-4"
        data-testid="blog-content"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mkComponents(isRTL, serifClass)}>
          {lang === "ar" ? post.content_ar : post.content_en}
        </ReactMarkdown>
      </div>
    </article>
  );
}
